import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Collection } from 'models/Collection';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';
import { saveCollection } from 'networking/mindfeed/services/collection.service';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { isContext } from 'utils/ContextUtils';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

type Context = {
  previousCollections: Collection[];
};

export type Variables = {
  collection: Collection;
};

export const useSaveCollection = ({
  onSuccess,
}: {
  onSuccess?: (data: Collection) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { selectedBox } = useActiveSpace();

  const onMutateCallback = async (
    variables: Variables,
  ): Promise<Context | undefined> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({
      queryKey: collectionsQuery(selectedBox?.id).queryKey,
    });

    // Snapshot the previous value
    const previousCollections = queryClient.getQueryData<Collection[]>(
      collectionsQuery(selectedBox?.id).queryKey,
    );

    // Whether the collection already exists in the stack
    const itExists =
      previousCollections instanceof Array &&
      previousCollections.some((c) => c.id === variables.collection.id);

    // Optimistically update to the new value
    if (itExists) {
      queryClient.setQueryData<Collection[]>(
        collectionsQuery(selectedBox?.id).queryKey,
        previousCollections.map((c) =>
          c.id !== variables.collection.id ? c : variables.collection,
        ),
      );
    } else {
      // We don't do anything because we don't have an ID to use as key
    }

    return { previousCollections } as Context;
  };

  const onSuccessCallback = async (
    collection: Collection,
    _variables: Variables,
    context: Context | undefined,
  ) => {
    // Whether the collection already exists in the stack
    const itExists = context.previousCollections.some(
      (c) => c.id === collection.id,
    );

    if (itExists) {
      // Update the recently updated collection
      queryClient.setQueryData<Collection[]>(
        collectionsQuery(selectedBox?.id).queryKey,
        context.previousCollections.map((c) =>
          c.id !== collection.id ? c : collection,
        ),
      );

      // Notify User
      notify(NotificationType.COLLECTION_UPDATED);
    } else {
      // Add the newly created collection
      queryClient.setQueryData(
        collectionsQuery(selectedBox?.id).queryKey,
        (data: Collection[]) => {
          return [...data, collection];
        },
      );

      // Notify User
      notify(NotificationType.COLLECTION_CREATED);
    }

    if (onSuccess) {
      onSuccess(collection);
    }
  };

  const onErrorCallback = (
    _err: any,
    _variables: Variables,
    context: Context | undefined,
  ) => {
    // If the mutation fails, use the context returned from onMutate to roll back
    if (isContext<Context>(context, 'previousCollections')) {
      queryClient.setQueryData<Collection[]>(
        collectionsQuery(selectedBox?.id).queryKey,
        context.previousCollections,
      );
    }
  };

  const saveCollectionMutation = useMutation<
    Collection,
    any,
    Variables,
    Context
  >({
    mutationFn: (variables) =>
      saveCollection(variables.collection, selectedBox),
    onMutate: onMutateCallback,
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });

  return saveCollectionMutation;
};
